import { useQuery } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppBanners,
  formatAllianceDate,
} from '../components/alliance/app-banners';
import {
  SystemIdentity,
  SystemSplash,
} from '../components/alliance/system-screen';
import {
  AuthFailScreen,
  MaintenanceScreen,
  MfaRequiredScreen,
  SessionExpiredScreen,
} from '../components/alliance/system-screens';
import { AppShellBar } from '../components/app-shell-bar';
import { Arrival } from '../components/arrival';
import { RouteErrorBoundary } from '../components/route-container/route-error-boundary';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import {
  useAllianceIdentity,
  useAuthorizeQuery,
} from '../hooks/use-alliance-identity';
import { useConferenceEventsQuery } from '../hooks/use-conference-events';
import { useSessionActions } from '../hooks/use-session-actions';
import { captureError } from '../utils/capture-error';
import { formatEventsData } from '../utils/format-events';
import { checkForRedirectPath } from '../utils/redirect';
import { SectionErrorBoundary } from '../components/section-error-boundary';

/**
 * True when an error means the Cognito session is gone (refresh token
 * expired or revoked). Amplify sometimes rejects with a plain string.
 */
const isSessionError = (error: unknown) => {
  const err = error as {
    code?: string;
    name?: string;
    message?: string;
    response?: { status?: number };
  } | null;
  if (err?.response?.status === 401) {
    return true;
  }
  const text = `${err?.code || ''} ${err?.name || ''} ${
    err?.message || (typeof error === 'string' ? error : '')
  }`;
  return /not authenticated|no current user|NotAuthorizedException|refresh token|token has expired|token has been revoked/i.test(
    text,
  );
};

/**
 * The shell around every page, for anonymous visitors and signed-in users
 * alike. In order:
 *
 * 1. Signing out: hold the splash until Cognito is done
 * 2. Splash while the session, the post-sign-in redirect, maintenance, MFA
 *    and (signed in) the Contracting /authorize load ("Signing you in…" on
 *    the return from Cognito)
 * 3. Maintenance, for everyone (as before)
 * 4. Session expired, when Cognito says the session is gone. Checked before
 *    MFA, so a dead session never lands on the MFA screen
 * 5. The MFA check failed for another reason: couldn't load (not "no MFA")
 * 6. MFA not set up: the MFA screen, which sends them to Contracting
 * 7. The app: App Bar, banners, the page (in a route error boundary keyed
 *    so "Try again" remounts it) and the Arrival bar
 *
 * /authorize never blocks: Events is public, so account status isn't gated.
 */
export function Root() {
  // Context
  const { state } = React.useContext(AuthContext);
  const appContext = React.useContext(AppContext);
  const { dispatch } = appContext;
  // State
  const [checking, setChecking] = React.useState(true);
  const [hasChecked, setHasChecked] = React.useState(false);
  const [fromCognito] = React.useState(() =>
    new URLSearchParams(window.location.search).has('code'),
  );
  const [maintData, setMaintData] = React.useState({
    checking: true,
    maintenanceActive: false,
    maintenanceBegin: '',
    maintenanceEnd: '',
  });
  // Hooks
  const navigate = useNavigate();
  const identity = useAllianceIdentity();
  const queryAuthorize = useAuthorizeQuery();
  const { signOut, signInAgain } = useSessionActions();

  React.useEffect(() => {
    const checkForMaintenance = async () => {
      try {
        if (process.env.REACT_APP_MAINT_URL) {
          const body = { data: { origin: window.origin } };
          const response = await fetch(process.env.REACT_APP_MAINT_URL, {
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
          if (!response.ok) {
            // The maintenance check fails open: never lock anyone out over it
            return;
          }
          const data: {
            data: {
              maintenanceActive: boolean;
              maintenanceBegin: string;
              maintenanceEnd: string;
            };
          } = await response.json();
          setMaintData((currentState) => ({ ...currentState, ...data.data }));
        }
      } catch (error) {
        captureError({ data: { error } });
      } finally {
        setMaintData((currentState) => ({ ...currentState, checking: false }));
      }
    };

    // Check for the path after the authentication context is done checking
    // if the user is signed in
    if (!state.isChecking) {
      checkForMaintenance();
    }
  }, [state.isChecking]);

  React.useEffect(() => {
    const checkForPath = async () => {
      try {
        setChecking(true);
        setHasChecked(true);
        if (state.user !== undefined) {
          // Check if there is a path stored in the browser to
          // redirect the user to after logging in
          const pathname = await checkForRedirectPath();
          if (pathname) {
            // Route the user to the path
            navigate(pathname);
          }
        } else if (!fromCognito) {
          // A visitor who left the Hosted UI without signing in: drop the
          // stored path. Following it would send them to a protected page,
          // which stores it again and redirects to sign-in on every visit
          await checkForRedirectPath();
        }
      } catch (error) {
        captureError({ data: { error } });
      } finally {
        setChecking(false);
      }
    };

    // Check for the path after the authentication context is done checking
    // if the user is signed in
    if (!state.isChecking && !hasChecked) {
      checkForPath();
    }
  }, [navigate, state.isChecking, state.user, fromCognito, hasChecked]);

  // Query - Published conferences, copied into AppContext for Home, Events
  // and the Arrival bar. Home and Events show its failure (it used to fail
  // silently and leave them loading forever)
  useConferenceEventsQuery({
    onSuccess: (data) =>
      dispatch({ type: 'SET_EVENTS', payload: formatEventsData({ data }) }),
    onError: (error) => captureError({ data: { error } }),
  });

  const isAuthenticated = state.user !== undefined;

  // Query - Check that the user has set up Multi-Factor Authentication for their account
  const queryMFA = useQuery({
    enabled: isAuthenticated,
    queryKey: ['check_Auth.getPreferredMFA'],
    queryFn: async () => {
      // Bypass Cache to always get the latest preferences for the Current User
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      const preferredMFA = await Auth.getPreferredMFA(currentUser);

      const mfaOptions = ['EMAIL_OTP', 'SMS_MFA', 'SOFTWARE_TOKEN_MFA'];

      // Determine if the user has selected one of the available options
      return mfaOptions.includes(preferredMFA || '');
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // We need to check the fetch status to see if the query is actually running
  const isLoadingMFA = queryMFA.isLoading && queryMFA.fetchStatus !== 'idle';
  const isAuthorizing =
    queryAuthorize.isLoading && queryAuthorize.fetchStatus !== 'idle';
  const isLoading = state.isChecking || checking || maintData.checking;

  // "Signed in as" on the full-page screens: name + number once /authorize
  // has answered, otherwise the Cognito username
  let systemIdentity: SystemIdentity | null = null;
  if (identity && queryAuthorize.isSuccess) {
    systemIdentity = {
      kind: 'full',
      name: identity.user.fullName,
      id: identity.user.id,
    };
  } else if (state.user) {
    systemIdentity = { kind: 'token', id: state.user.getUsername() };
  }

  const sessionExpired =
    (queryMFA.isError && isSessionError(queryMFA.error)) ||
    (queryAuthorize.isError && isSessionError(queryAuthorize.error)) ||
    (state.tokenError && queryMFA.isError);

  if (isAuthenticated && state.signingOut) {
    // Hold here until Cognito has signed out (it redirects back signed out)
    return <SystemSplash caption="Signing you out…" />;
  } else if (isLoading || isLoadingMFA || isAuthorizing) {
    return (
      <SystemSplash caption={fromCognito ? 'Signing you in…' : undefined} />
    );
  } else if (maintData.maintenanceActive) {
    return (
      <MaintenanceScreen
        until={formatAllianceDate({
          value: maintData.maintenanceEnd,
          long: true,
        })}
        identity={systemIdentity}
        onSignOut={isAuthenticated ? signOut : undefined}
      />
    );
  } else if (isAuthenticated && sessionExpired) {
    return (
      <SessionExpiredScreen
        // A failed redirect is reported by signInAgain; the screen stays
        onSignIn={() => signInAgain().catch(() => undefined)}
      />
    );
  } else if (isAuthenticated && queryMFA.isError) {
    // The MFA check failed for another reason: that isn't "no MFA"
    return (
      <AuthFailScreen
        identity={systemIdentity}
        onRetry={() => queryMFA.refetch()}
        onSignOut={signOut}
      />
    );
  } else if (isAuthenticated && queryMFA.isSuccess && !queryMFA.data) {
    return <MfaRequiredScreen identity={systemIdentity} onSignOut={signOut} />;
  }

  return (
    <div>
      <AppShellBar />
      <AppBanners />

      <RouteErrorBoundary>
        <React.Fragment key={appContext.state.routeKey}>
          <Outlet />
        </React.Fragment>
      </RouteErrorBoundary>

      {/* The Arrival bar sits outside the page boundary: a crash in it shows
          the section error over the page instead of the full-page crash */}
      <SectionErrorBoundary floating>
        <Arrival />
      </SectionErrorBoundary>
    </div>
  );
}
